import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ContentStatusBadge } from '@tms/shared-ui';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  deleteExercise,
  listExercises,
  updateExercise,
  type ExerciseRow,
} from '../services/exerciseApi';
import { ContentPageLayout } from '../components/Shared';

const APP_ORIGIN = (import.meta.env.VITE_APP_ORIGIN as string) || 'http://app.localtest.me:3001';
const STATUSES = ['draft', 'published', 'archived'];

const ExercisesAdminPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [exercises, setExercises] = useState<ExerciseRow[]>([]);
  const [search, setSearch] = useState('');
  const [edit, setEdit] = useState<ExerciseRow | null>(null);
  const [form, setForm] = useState({
    title: '',
    code: '',
    description: '',
    status: 'draft',
    isPublic: false,
    passing_threshold: 70,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listExercises({ limit: 500, sortBy: 'updatedAt', sortOrder: 'desc' });
      setExercises(res.data?.exercises || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load exercises');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = exercises.filter(
    (ex) =>
      !search.trim() ||
      ex.title?.toLowerCase().includes(search.toLowerCase()) ||
      ex.code?.toLowerCase().includes(search.toLowerCase())
  );

  const openEdit = (ex: ExerciseRow) => {
    setEdit(ex);
    setForm({
      title: ex.title || '',
      code: ex.code || '',
      description: ex.description || '',
      status: ex.status || 'draft',
      isPublic: !!ex.isPublic,
      passing_threshold: ex.passing_threshold ?? 70,
    });
  };

  const handleSave = async () => {
    if (!edit) return;
    try {
      await updateExercise(edit._id, form);
      setEdit(null);
      setMsg('Exercise updated.');
      await load();
    } catch (e: any) {
      setMsg(e?.message || 'Update failed');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this exercise?')) return;
    try {
      await deleteExercise(id);
      setMsg('Exercise deleted.');
      await load();
    } catch (e: any) {
      setMsg(e?.message || 'Delete failed');
    }
  };

  return (
    <ContentPageLayout>
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={2}>
        <Box>
          <Typography variant="h5" color="primary" fontWeight="bold">Exercise manager</Typography>
          <Typography variant="body2" color="text.secondary">
            List and edit metadata. Use the main app for the full question editor.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<OpenInNewIcon />}
          href={`${APP_ORIGIN.replace(/\/$/, '')}/exercises`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open in app
        </Button>
      </Box>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {msg ? <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg}</Alert> : null}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search by title or code…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Questions</TableCell>
              <TableCell>Attempts</TableCell>
              <TableCell>Avg score</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((ex) => (
              <TableRow key={ex._id}>
                <TableCell>{ex.title || '—'}</TableCell>
                <TableCell>{ex.code || '—'}</TableCell>
                <TableCell><ContentStatusBadge status={ex.status || 'draft'} /></TableCell>
                <TableCell>{ex.questions?.length ?? 0}</TableCell>
                <TableCell>{ex.totalAttempts ?? 0}</TableCell>
                <TableCell>{ex.averageScore != null ? `${Math.round(ex.averageScore)}%` : '—'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(ex)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(ex._id)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={!!edit} onClose={() => setEdit(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit exercise</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
            <TextField label="Code" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} />
            <TextField label="Description" multiline value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            <TextField select label="Status" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
              {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField
              label="Passing threshold %"
              type="number"
              value={form.passing_threshold}
              onChange={(e) => setForm((f) => ({ ...f, passing_threshold: parseInt(e.target.value, 10) || 0 }))}
            />
            <TextField
              select
              label="Public"
              value={form.isPublic ? 'yes' : 'no'}
              onChange={(e) => setForm((f) => ({ ...f, isPublic: e.target.value === 'yes' }))}
            >
              <MenuItem value="no">No</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdit(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
    </ContentPageLayout>
  );
};

export default ExercisesAdminPage;
